// src/pages/CollectionList.js
import React, { useEffect, useState } from "react";
import { useAuthCheck } from "../components/Auth";
import { api } from "../components/Api";
import Loading from "../components/Loading";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

export default function CollectionList() {
    const [collections, setCollections] = useState([]);
    const [licenses, setLicenses] = useState([]);
    const [selectedLicense, setSelectedLicense] = useState("");
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { checkToken } = useAuthCheck();

    useEffect(() => { checkToken(); }, [checkToken]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [colRes, licRes] = await Promise.all([
                api.get("/collections"),
                api.get("/license-cards"),
            ]);
            setCollections(colRes.data);
            setLicenses(licRes.data);
        } catch (err) {
            console.error(err);
            alert("Erreur lors de la récupération des collections.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Voulez-vous vraiment supprimer cette collection ?")) return;

        try {
            await api.delete(`/collections/${id}`);
            alert("Collection supprimée avec succès.");
            setCollections((prev) => prev.filter((c) => c.id !== id));
        } catch (err) {
            console.error(err);
            if (err.response?.status === 409) {
                alert("Impossible de supprimer cette collection car elle est utilisée par des produits.");
            } else {
                alert("Erreur lors de la suppression de la collection.");
            }
        }
    };

    const token = localStorage.getItem("token");
    if (!token || localStorage.getItem("role") > 2) {
        navigate("/");
    }

    const filteredCollections = selectedLicense
        ? collections.filter(c => c.license_card_id === parseInt(selectedLicense))
        : collections;

    return (
        <div className="container mx-auto p-6">
            <h1 className="text-2xl font-bold mb-4 dark:text-white">Liste des Collections</h1>

            <div className="flex items-center space-x-4 mb-4">
                <button
                    onClick={() => navigate("/add-collection")}
                    className="bg-blue-400 hover:bg-blue-500 text-white font-semibold rounded px-4 py-2"
                >
                    Ajouter une collection
                </button>

                <select
                    value={selectedLicense}
                    onChange={e => setSelectedLicense(e.target.value)}
                    className="border p-2 rounded  dark:bg-gray-700 bg-white"
                >
                    <option value="">-- Filtrer par licence --</option>
                    {licenses.map(l => (
                        <option key={l.id} value={l.id}>{l.license_card}</option>
                    ))}
                </select>
            </div>

            {loading ? (
                <Loading message="Chargement des collections..." />
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse border text-sm text-center">
                        <thead className="bg-gray-100 dark:bg-gray-800">
                            <tr>
                                <th className="border px-2 py-1">Licence</th>
                                <th className="border px-2 py-1">Nom de la collection</th>
                                <th className="border px-2 py-1">Supprimer</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCollections.map((c, index) => {
                                const bgColor = index % 2 === 0 ? "dark:bg-gray-600 bg-gray-050" : "dark:bg-gray-700 bg-gray-100";
                                return (
                                    <tr key={c.id} className={`${bgColor}`}>
                                        <td className="border px-2 py-1">{c.license_card?.license_card || ""}</td>
                                        <td className="border px-2 py-1">{c.collection_name}</td>
                                        <td className="border px-2 py-1">
                                            <button
                                                onClick={() => handleDelete(c.id)}
                                                className="text-red-500 hover:text-red-700 text-2xl"
                                            >
                                                <FontAwesomeIcon icon={faTrashCan} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredCollections.length === 0 && (
                                <tr>
                                    <td colSpan="3" className="text-gray-500 py-3">
                                        Aucune collection trouvée pour cette licence.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
